"use client";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchEmailVerifiedStatus } from "../Slice/EmailVerification/EmailVerificationSlice";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Icon,
  useColorModeValue,
  Divider,
  ScaleFade,
  keyframes,
} from "@chakra-ui/react";
import {
  CheckCircleIcon,
  WarningIcon,
  EmailIcon,
  ArrowForwardIcon,
} from "@chakra-ui/icons";
import {
  fetchBasicDetailsAsync,
  selectBasicDetails,
} from "../../profile/BasicDetailsController/BasicDetailsSlice";

// Animation keyframes
const successPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0.7); }
  70% { box-shadow: 0 0 0 20px rgba(72, 187, 120, 0); }
  100% { box-shadow: 0 0 0 0 rgba(72, 187, 120, 0); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-20px); }
  60% { transform: translateY(-10px); }
`;

const VerifiedPage = () => {
  const { token } = useParams(); // Extract token from URL
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const details = useSelector(selectBasicDetails);

  const [isVerified, setIsVerified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  console.log("details", details)

  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const cardBgGradient = useColorModeValue(
    "linear(to-br, white, gray.50)",
    "linear(to-br, gray.800, gray.900)"
  );
  const primaryColor = useColorModeValue("blue.500", "blue.300");
  const headingColor = useColorModeValue("blue.600", "blue.300");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const iconBgColor = useColorModeValue("blue.50", "blue.900");
  const successIconBgColor = useColorModeValue("green.50", "green.900");
  const errorIconBgColor = useColorModeValue("red.50", "red.900");

  useEffect(() => {
    // console.log("token", token)
    if (!token) return;

    // First, fetch basic details if not already available
    if (!details) {
      dispatch(fetchBasicDetailsAsync());
    }

    // If details are available, check email verification status
    if (details && details.email_verify !== undefined) {
      if (details.email_verify === 1) {
        // Email already verified, show option for home redirection
        setIsVerified(true);
        setLoading(false);
      } else if (details.email_verify === 0) {
        // Email not verified, try to verify with token
        const verifyEmail = async () => {
          try {
            const response = await dispatch(
              fetchEmailVerifiedStatus(token)
            ).unwrap();
            setIsVerified(response?.verified || false);
          } catch (err) {
            setError("Verification failed. Please try again.");
          } finally {
            setLoading(false);
          }
        };

        verifyEmail();
      }
    } else {
      // If details aren't available yet, fetch them
      dispatch(fetchBasicDetailsAsync());
    }
  }, [token, dispatch, details]);

  return (
    <Container
      centerContent
      py={10}
      maxW="100vw"
      height="100vh"
      bg={useColorModeValue("gray.50", "gray.900")}
    >
      <div>
        <div className="bg-mainBg p-3">
          <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
            <div className="rounded-xl sm:px-28">

              <Center h="full">
                <ScaleFade initialScale={0.9} in={true}>
                  <Box
                    p={8}
                    borderRadius="2xl"
                    boxShadow="2xl"
                    bgGradient={cardBgGradient}
                    width="100%"
                    maxWidth="500px"
                    borderWidth="1px"
                    borderColor={useColorModeValue("gray.200", "gray.700")}
                    position="relative"
                    overflow="hidden"
                    _before={{
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "8px",
                      bgGradient: "linear(to-r, blue.400, purple.500, pink.400)",
                    }}
                  >
                    <VStack spacing={6} align="center">
                      <Heading
                        size="lg"
                        textAlign="center"
                        color={headingColor}
                        fontWeight="extrabold"
                        letterSpacing="tight"
                      >
                        Email Verification Status
                      </Heading>

                      <Divider />

                      {loading ? (
                        <VStack spacing={6} py={8}>
                          <Spinner
                            thickness="4px"
                            speed="0.65s"
                            emptyColor="gray.200"
                            color="blue.500"
                            size="xl"
                          />
                          <Text color={textColor} fontSize="md">
                            Checking verification status...
                          </Text>
                        </VStack>
                      ) : error ? (
                        <Alert
                          status="error"
                          variant="subtle"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          textAlign="center"
                          borderRadius="lg"
                          py={6}
                        >
                          <AlertIcon boxSize="40px" mr={0} />
                          <AlertTitle mt={4} mb={1} fontSize="lg">
                            Verification Failed
                          </AlertTitle>
                          <AlertDescription maxWidth="sm" fontSize="md">
                            {error}
                          </AlertDescription>
                        </Alert>
                      ) : isVerified ? (
                        <VStack spacing={6} w="full">
                          <Box
                            p={5}
                            borderRadius="full"
                            bg={successIconBgColor}
                            boxShadow="md"
                            animation={`${successPulse} 2s infinite`}
                          >
                            <Icon
                              as={CheckCircleIcon}
                              w={16}
                              h={16}
                              color="green.500"
                            />
                          </Box>

                          <Alert
                            status="success"
                            variant="subtle"
                            borderRadius="lg"
                            boxShadow="md"
                          >
                            <AlertIcon />
                            <Box>
                              <AlertTitle fontSize="lg">Success!</AlertTitle>
                              <AlertDescription fontSize="md">
                                Your email is verified! 🎉
                              </AlertDescription>
                            </Box>
                          </Alert>

                          {details?.email_verify === 1 && (
                            <Box
                              bg="blue.50"
                              p={4}
                              borderRadius="lg"
                              width="full"
                              textAlign="center"
                              boxShadow="sm"
                            >
                              <Text fontSize="sm" color="blue.700">
                                Your email was already verified in our system.
                              </Text>
                            </Box>
                          )}
                        </VStack>
                      ) : (
                        <VStack spacing={6} w="full">
                          <Box
                            p={5}
                            borderRadius="full"
                            bg={errorIconBgColor}
                            boxShadow="md"
                          >
                            <Icon
                              as={WarningIcon}
                              w={16}
                              h={16}
                              color="red.500"
                              animation={`${bounce} 2s ease infinite`}
                            />
                          </Box>

                          <Alert
                            status="error"
                            variant="subtle"
                            borderRadius="lg"
                            boxShadow="md"
                          >
                            <AlertIcon />
                            <Box>
                              <AlertTitle fontSize="lg">Not Verified</AlertTitle>
                              <AlertDescription fontSize="md">
                                Your email is NOT verified. Please check your inbox.
                              </AlertDescription>
                            </Box>
                          </Alert>

                          <Box
                            bg="blue.50"
                            p={4}
                            borderRadius="lg"
                            width="full"
                            textAlign="center"
                            boxShadow="sm"
                          >
                            <Flex align="center" justify="center">
                              <Icon as={EmailIcon} mr={2} color="blue.500" />
                              <Text fontSize="sm" color="blue.700">
                                Check your inbox and spam folder for the verification
                                email.
                              </Text>
                            </Flex>
                          </Box>
                        </VStack>
                      )}

                      <Button
                        colorScheme="blue"
                        size="lg"
                        height="60px"
                        width="full"
                        onClick={() => navigate("/")}
                        rightIcon={<ArrowForwardIcon />}
                        _hover={{
                          transform: "translateY(-3px)",
                          boxShadow: "xl",
                          bgGradient: "linear(to-r, blue.500, blue.600)",
                        }}
                        bgGradient="linear(to-r, blue.400, blue.500)"
                        transition="all 0.3s"
                        borderRadius="lg"
                        fontWeight="bold"
                        fontSize="md"
                        mt={4}
                      >
                        Go Home
                      </Button>
                    </VStack>
                  </Box>
                </ScaleFade>
              </Center>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default VerifiedPage;